import { Box, TextField } from "@mui/material";
import ConnectedCharacterInputs from "../../../../../../permitForms/ConnectedCharacterInputs";
import { getConnectionInputError } from "../../../../../../../utils/fieldConnections";
import { useCallback, useEffect, useRef, useState } from "react";
import { getPreviewFieldLabel } from "./PreviewForm";

const deriveLabel = (field) => getPreviewFieldLabel(field);

const PreviewCharacterInput = ({
  field,
  onCommit,
  onLabelCommit,
  disabled,
  demoVersion,
}) => {
  const label = deriveLabel(field);
  const [draft, setDraft] = useState(String(field.value ?? ""));
  const [localLabel, setLocalLabel] = useState(label);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const labelEditorRef = useRef(null);
  const labelDraftRef = useRef(label);
  const isEditingLabelRef = useRef(false);

  useEffect(() => {
    setDraft(String(field.value ?? ""));
  }, [field.name, field.value, demoVersion]);

  const commitLabel = useCallback(() => {
    if (!isEditingLabelRef.current) return;

    const nextLabel = labelDraftRef.current;
    isEditingLabelRef.current = false;
    labelEditorRef.current?.blur();
    onLabelCommit?.(field, nextLabel);
    setLocalLabel(nextLabel.trim() || label);
    setIsEditingLabel(false);
  }, [field, label, onLabelCommit]);

  useEffect(() => {
    if (!isEditingLabel) return undefined;

    const id = requestAnimationFrame(() => {
      const element = labelEditorRef.current;
      if (!element) return;
      element.focus();
      const end = element.value.length;
      element.setSelectionRange(end, end);
    });

    return () => cancelAnimationFrame(id);
  }, [isEditingLabel]);

  useEffect(() => {
    if (!isEditingLabel) return undefined;

    const handleClickAway = (event) => {
      if (labelEditorRef.current?.contains(event.target)) return;
      commitLabel();
    };

    document.addEventListener("pointerdown", handleClickAway, true);
    return () =>
      document.removeEventListener("pointerdown", handleClickAway, true);
  }, [commitLabel, isEditingLabel]);

  const startEditingLabel = (event) => {
    if (!disabled || !onLabelCommit || isEditingLabel) return;

    event.preventDefault();
    event.stopPropagation();
    labelDraftRef.current = label;
    isEditingLabelRef.current = true;
    setLocalLabel(label);
    setIsEditingLabel(true);
  };

  if (isEditingLabel) {
    return (
      <TextField
        inputRef={labelEditorRef}
        inputProps={{ "data-preview-label-editor": "true" }}
        variant="filled"
        fullWidth
        margin="normal"
        label="Field label"
        value={localLabel}
        autoFocus
        onChange={(event) => {
          labelDraftRef.current = event.target.value;
          setLocalLabel(event.target.value);
        }}
        onBlur={commitLabel}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitLabel();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            isEditingLabelRef.current = false;
            setIsEditingLabel(false);
          }
        }}
      />
    );
  }

  if (disabled) {
    const error = getConnectionInputError(field, draft);

    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <TextField
          fullWidth
          variant="filled"
          margin="normal"
          type="text"
          name={field.name}
          label={label}
          required={Boolean(field.required)}
          value={draft}
          inputProps={{ readOnly: true }}
          error={Boolean(error)}
          helperText={error || undefined}
          onDoubleClick={startEditingLabel}
        />
      </Box>
    );
  }

  return (
    <Box
      onBlur={(event) => {
        // Commit only when focus leaves this input wrapper.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (disabled) return;

        if (draft !== String(field.value ?? "")) {
          onCommit(field.name, draft);
        }
      }}
    >
      <ConnectedCharacterInputs
        sources={[field]}
        values={{ [field.name]: draft }}
        disabled={disabled}
        onChange={(_, value) => setDraft(value)}
      />
    </Box>
  );
};

export default PreviewCharacterInput;
